import { MouseEvent, broadcastEvent } from "./events";
import type { Stage } from "./display";

const INPUT_SLOT_COUNT = 10;
const stageMouse = { x: 0, y: 0 };

type StagePressHandler = (stageX: number, stageY: number) => void;

/** Shared state for pointer-driven game controls. */
export class PointerInput {
  isDown(slot: number): boolean {
    return this.held[slot] === 1 || this.pressed[slot] === 1;
  }

  setHeld(slot: number, down: boolean): void {
    if (slot < 0 || slot >= INPUT_SLOT_COUNT) return;
    this.held[slot] = down ? 1 : 0;
  }

  press(slot: number): void {
    if (slot < 0 || slot >= INPUT_SLOT_COUNT) return;
    this.pressed[slot] = 1;
  }

  releasePulses(): void {
    this.pressed.fill(0);
  }

  clear(): void {
    this.held.fill(0);
    this.pressed.fill(0);
  }

  setStagePressHandler(handler: StagePressHandler): void {
    this.stagePressHandler = handler;
  }

  $stagePress(stageX: number, stageY: number): void {
    this.stagePressHandler?.(stageX, stageY);
  }

  private readonly held = new Uint8Array(INPUT_SLOT_COUNT);
  private readonly pressed = new Uint8Array(INPUT_SLOT_COUNT);
  private stagePressHandler: StagePressHandler | null = null;
}

/**
 * Routes pointer events into Flash-compatible broadcasts and stage-space game
 * controls. Pointer Events cover mouse, pen and touch with one coordinate path.
 */
export function bindMouse(
  canvas: HTMLCanvasElement,
  stage: Stage,
): PointerInput {
  const input = new PointerInput();
  const toStage = (e: PointerEvent | globalThis.MouseEvent): boolean => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    stageMouse.x = ((e.clientX - rect.left) / rect.width) * stage.stageWidth;
    stageMouse.y = ((e.clientY - rect.top) / rect.height) * stage.stageHeight;
    return true;
  };

  canvas.addEventListener("pointermove", (e) => {
    if (!toStage(e)) return;
    broadcastEvent(
      new MouseEvent(MouseEvent.MOUSE_MOVE, stageMouse.x, stageMouse.y),
    );
  });
  canvas.addEventListener("pointerdown", (e) => {
    if (e.button !== 0 || !toStage(e)) return;
    e.preventDefault();
    canvas.setPointerCapture(e.pointerId);
    broadcastEvent(
      new MouseEvent(MouseEvent.MOUSE_DOWN, stageMouse.x, stageMouse.y),
    );
    input.$stagePress(stageMouse.x, stageMouse.y);
  });
  canvas.addEventListener("pointerup", (e) => {
    if (e.button !== 0 || !toStage(e)) return;
    broadcastEvent(
      new MouseEvent(MouseEvent.MOUSE_UP, stageMouse.x, stageMouse.y),
    );
  });
  canvas.addEventListener("click", (e) => {
    if (!toStage(e)) return;
    broadcastEvent(
      new MouseEvent(MouseEvent.CLICK, stageMouse.x, stageMouse.y),
    );
  });
  window.addEventListener("blur", () => input.clear());
  return input;
}
